import { useMutation, useQuery } from '@tanstack/react-query';

import { EXCLOUD_STUDYMAP_ID } from '@/shared/constants/constants';
import { getDataImportApi, getTextbookLcms, getDataImportLcms } from '@/shared/api/create';

// const useTextbookList = (param: API.TextbookList.Req) =>
//   useQuery({
//     queryKey: ['textbookList', param],
//     queryFn: () => getTextbook.getTextbookList(param),
//   });

const useTextbookList = (param: API.TextbookList.Req, enabled: boolean) =>
    useQuery({
        queryKey: ['textbookList', param],
        queryFn: () => getTextbookLcms.getTextbookList(param),
        enabled: enabled,
    });

const useTextbookSubject = () =>
    useMutation({
        mutationKey: ['textbook', 'subject'],
        mutationFn: (param: API.TextbookSubject.Req) => getTextbookLcms.postTextbookSubject(param),
    });

const useDataDetailInfo = (param: API.LesnRscInfo.Req, enabled?: boolean, queryKey?: any) =>
    useQuery({
        queryKey: ['detail', 'data', param, queryKey],
        queryFn: () => getDataImportApi.getLesnRscInfo(param),
        enabled: enabled,
    });

const usePostTextbookList = () =>
    useMutation({
        mutationKey: ['textbook', 'textbooklist'],
        mutationFn: (param: API.TextbookList.Req) => getTextbookLcms.postTextbookList(param),
    });

const usePostTextbookListbyCurriBook = () =>
    useMutation({
        mutationKey: ['textbook', 'textbooklist'],
        mutationFn: (param: API.TextbookListbyCurriBook.Req) =>
            getTextbookLcms.postTextbookListbyCurriBook(param),
    });

const usePostCurriBookMeta = () =>
    useMutation({
        mutationKey: ['textbook', 'curriBookMeta'],
        mutationFn: (param: API.CurriBookMeta.Req) => getTextbookLcms.postCurriBookMeta(param),
    });

const useMetaList = (param: API.MetaList.Req) =>
    useQuery({
        queryKey: ['metaList', param],
        queryFn: () => getTextbookLcms.getMetaList(param),
    });

const useSubDepthList = (param: API.MetaList.Req) =>
    useQuery({
        queryKey: ['subList', param],
        queryFn: async () => {
            const res = await getTextbookLcms.getMetaList(param);
            if (!res) throw new Error('API returned undefined');
            return res; // undefined 방지
        },
        enabled: !!param.parent_id && param.parent_id !== 0,
        select: (res: API.MetaList.Res) =>
            res.vo.filter(
                (item) => item.is_active === true && !EXCLOUD_STUDYMAP_ID.includes(item.id)
            ),
        staleTime: 0, // 매번 새롭게 요청 (데이터가 항상 신선하다고 간주)
    });

const useMetaDataType = () =>
    useMutation({
        mutationKey: ['import', 'metaSearch'],
        mutationFn: (param: API.MetaSearch.Req) => getDataImportLcms.postMetaSearch(param),
    });

const useMetaDataEvalArea = () =>
    useMutation({
        mutationKey: ['import', 'useMetaDataEvalArea'],
        mutationFn: (param: API.MetaSearch.Req) => getDataImportLcms.postMetaSearch(param),
    });

const useMetaDifficulty = () =>
    useMutation({
        mutationKey: ['import', 'metaDifficulty'],
        mutationFn: (param: API.MetaSearch.Req) => getDataImportLcms.postMetaSearch(param),
    });

const useStudyMap = () =>
    useMutation({
        mutationKey: ['import', 'studyMap'],
        mutationFn: (param: API.StudyMap.Req) => getDataImportLcms.postStudyMap(param),
    });

const useDescendants = (mapCode: string) =>
    useQuery({
        queryKey: ['descendants', mapCode],
        queryFn: () => getDataImportLcms.getDescendants(mapCode),
        enabled: typeof mapCode === 'string' && mapCode.trim() !== '',
        select: (data) =>
            data?.vo.filter(
                (item) => item.is_active === true && !EXCLOUD_STUDYMAP_ID.includes(item.id)
            ),
    });

const useLesnRscExamScope = (param: API.LesnRscExamScope.Req) =>
    useQuery({
        queryKey: ['learn-scope', param],
        queryFn: () => getDataImportApi.getLesnRscExamScope(param),
    });

const useSetSearchEx = () =>
    useMutation({
        mutationKey: ['import', 'setSearch'],
        mutationFn: (param: API.SetSearchEx.Req) => getDataImportLcms.postSetSearchEx(param),
    });

const useArticleSearchEx = () =>
    useMutation({
        mutationKey: ['import', 'articleSearch'],
        mutationFn: (param: API.ArticleSearchEx.Req) =>
            getDataImportLcms.postArticleSearchEx(param),
    });

const useArticleCount = () =>
    useMutation({
        mutationKey: ['import', 'articleCount'],
        mutationFn: (param: API.ArticleCount.Req) => getDataImportLcms.postArticleCount(param),
    });

const useSetForSave = () =>
    useMutation({
        mutationKey: ['import', 'setForSave'],
        mutationFn: (param: API.SetForSave.Req) => getDataImportLcms.postSetForSave(param),
    });

const useSetSummaryForSave = () =>
    useMutation({
        mutationKey: ['import', 'setSummaryForSave'],
        mutationFn: (param: API.SetSummaryForSave.Req) =>
            getDataImportLcms.postSetSummaryForSave(param),
    });

const useSetMetaInfo = () =>
    useMutation({
        mutationKey: ['import', 'setMetaInfo'],
        mutationFn: (param: API.SetMetaInfo.Req) => getDataImportLcms.postSetMetaInfo(param),
    });

const useArticleInfo = () =>
    useMutation({
        mutationKey: ['import', 'articleInfo'],
        mutationFn: (param: API.ArticleInfo.Req) => getDataImportLcms.postArticleInfo(param),
    });

const useArticleMetaInfo = () =>
    useMutation({
        mutationKey: ['import', 'articleMetaInfo'],
        mutationFn: (param: API.ArticleMetaInfo.Req) =>
            getDataImportLcms.postArticleMetaInfo(param),
    });

const useContentsCopySet = () =>
    useMutation({
        mutationKey: ['import', 'contentsCopySet'],
        mutationFn: (param: API.CopySet.Req) => getDataImportLcms.postContentsCopySet(param),
    });

// createQueries 객체에서 커스텀 훅을 반환
export const createQueries = {
    textbook: {
        getTextbookList: useTextbookList,
        postTextbookSubject: useTextbookSubject,
        postTextbookList: usePostTextbookList,
        getMetaList: useMetaList,
        getSubList: useSubDepthList,
        postTextbookListbyCurriBook: usePostTextbookListbyCurriBook,
        postCurriBookMeta: usePostCurriBookMeta,
    },
    getLesnRscExamScope: useLesnRscExamScope,

    detail: {
        getDataDetailInfo: useDataDetailInfo,
    },
    import: {
        postMetaSearchDataTypeList: useMetaDataType,
        postMetaSearchDataEvalAreaList: useMetaDataEvalArea,
        postMetaSearchDifficultyList: useMetaDifficulty,
        postStudyMap: useStudyMap,
        getDescendants: useDescendants,
        postSetSearchEx: useSetSearchEx,
        postArticleSearchEx: useArticleSearchEx,
        postSetForSave: useSetForSave,
        postSetSummaryForSave: useSetSummaryForSave,
        postSetMetaInfo: useSetMetaInfo,
        postArticleInfo: useArticleInfo,
        postArticleMetaInfo: useArticleMetaInfo,
        postContentsCopySet: useContentsCopySet,
        postArticleCount: useArticleCount,
    },
};
